import { useEffect, useState, useCallback } from 'react';

import { CircularProgress, InputAdornment } from '@mui/material';
import { useStore } from '@tanstack/react-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import {
  LockedExpenseMessage,
  LockedReasonEnum,
} from '../../components/LockedExpenseMessage';
import { ExpenseFormType } from '../../types';
import { useGetUserMe } from '@/api/REST/userMe';
import {
  getCustomerSelectOptionById,
  getSearchCustomerSelectOptions,
  useGetCustomerOptionById,
} from '@/api/typesense/customers';
import {
  type EmployeeSelectOption,
  getEmployeeSelectOptionById,
  getSearchEmployeeSelectOptions,
} from '@/api/typesense/employees';
import {
  getProjectSelectOptionById,
  getSearchProjectSelectOptions,
  type ProjectSelectOption,
} from '@/api/typesense/projects';
import {
  getProjectSelectOptionByIdFromUnion,
  getSearchProjectSelectOptionsFromUnion,
} from '@/api/typesense/projects/projects';
import {
  getProjectSubcontractSelectOptionById,
  getSearchProjectSubcontractSelectOptions,
  useGetDefaultProjectSubcontractOptionByProjectId,
  useGetProjectSubcontractOptionById,
  useGetProjectSubcontractOptionsByProjectId,
  type ProjectSubcontractSelectOption,
} from '@/api/typesense/projectSubcontracts';
import { TypesenseCollection } from '@/api/typesense/types';
import { InputFieldSkeletonLoader } from '@/components/ui/InputFieldSkeletonLoader';
import { PageSection } from '@/components/ui/PageSection';
import { isRequired } from '@/forms/schemas';
import {
  requiredSelectOption,
  optionalSelectOption,
} from '@/forms/schemas/definitions';
import { BaseSelectOption } from '@/forms/types';
import { withFieldGroup } from '@/forms/useAppForm';
import { useUserAccountHasFeature } from '@/hooks/useUserAccountHasFeature';
import { useUserHasPrivilege } from '@/hooks/useUserHasPrivilege';

export type ProjectInformationFormFields = {
  employee: EmployeeSelectOption;
  customer?: BaseSelectOption;
  project: ProjectSelectOption;
  contract?: ProjectSubcontractSelectOption;
};

export const projectInformationDefaultValues: ProjectInformationFormFields = {
  employee: null,
  customer: null,
  project: null,
  contract: null,
};

export const projectInformationSchema = z.object({
  employee: requiredSelectOption({
    legalEntityCurrencyIsoCode: z.string(),
    legalEntityId: z.number(),
  }),
  customer: optionalSelectOption(),
  project: requiredSelectOption({
    currencyIsoCode: z.string(),
  }),
  contract: requiredSelectOption({
    isBillable: z.boolean(),
  }),
});

export type ProjectInformationOutput = z.infer<typeof projectInformationSchema>;

interface ProjectInformationFormProps extends Record<string, unknown> {
  title: string;
  expenseType: ExpenseFormType;
  formLocked?: boolean;
  lockedReason?: LockedReasonEnum;
  isParentLoading?: boolean;
}

export const ProjectInformationForm = withFieldGroup({
  defaultValues: projectInformationDefaultValues,
  props: {
    // These default values are also for type-checking and are not used at runtime
    title: 'Title',
    expenseType: 'personal',
    formLocked: undefined,
    lockedReason: undefined,
    isParentLoading: undefined,
  } as ProjectInformationFormProps,
  render: function Render({
    group,
    title,
    expenseType,
    formLocked,
    lockedReason,
    isParentLoading,
  }) {
    const { t } = useTranslation(['forms', 'expenses']);

    const [hasInitiallyLoaded, setHasInitiallyLoaded] = useState(false);

    const { hasPrivilege: hasAllRegistrationsPrivilege } = useUserHasPrivilege(
      'RegistrationAllRegistrations',
    );
    const { hasPrivilege: hasLegalEntityPrivilege } = useUserHasPrivilege(
      'RegistrationEmployeesInMyOwnLegalEntity',
    );
    const { hasFeature: hasMultiLegalEntitiesFeature } =
      useUserAccountHasFeature('multiLegalEntities');
    const { hasPrivilege: hasDepartmentPrivilege } = useUserHasPrivilege(
      'RegistrationEmployeesInMyOwnDepartmentAndSubDepartments',
    );
    const { hasFeature: hasDepartmentsFeature } =
      useUserAccountHasFeature('departments');

    const currentUserCanCreateExpensesForOthers =
      hasAllRegistrationsPrivilege ||
      (hasLegalEntityPrivilege && hasMultiLegalEntitiesFeature) ||
      (hasDepartmentPrivilege && hasDepartmentsFeature);

    const { hasFeature: allEmployeesCanRegisterOnAllActiveProjects } =
      useUserAccountHasFeature('allEmployeesRegisterOnAllActiveProjects');

    // on new expense, we want the user's own employee option to be pre-selected, so we wait for the loading to have happened here
    const { isLoading: isUserMeLoading } = useGetUserMe();

    const selectedEmployee = useStore(
      group.store,
      (state) => state.values.employee,
    );

    const selectedCustomer = useStore(
      group.store,
      (state) => state.values.customer,
    );

    const selectedProject = useStore(
      group.store,
      (state) => state.values.project,
    ) as ProjectSelectOption;

    const getProjectsViewKeyFilter = useCallback(() => {
      if (!allEmployeesCanRegisterOnAllActiveProjects) {
        if (selectedCustomer && selectedEmployee) {
          return `CustomerId:=${selectedCustomer.id} && $employeeallocations(UserId:=${selectedEmployee.id})`;
        } else if (selectedCustomer && !selectedEmployee) {
          return `CustomerId:=${selectedCustomer.id}`;
        } else if (!selectedCustomer && selectedEmployee) {
          return `$employeeallocations(UserId:=${selectedEmployee.id})`;
        } else {
          return undefined;
        }
      } else {
        if (selectedCustomer) {
          return `CustomerId:=${selectedCustomer.id}`;
        } else {
          return undefined;
        }
      }
    }, [
      selectedCustomer,
      selectedEmployee,
      allEmployeesCanRegisterOnAllActiveProjects,
    ]);

    const getProjectsKeyFilter = useCallback(() => {
      if (selectedCustomer) {
        return `CustomerId:=${selectedCustomer.id}`;
      } else {
        return undefined;
      }
    }, [selectedCustomer]);

    /*
     * this is only needed for the contract pre-selection logic in this component
     * the data fetching for the select options is done by the component itself
     */
    const { data: contractsOptions = [], isLoading: isContractsLoading } =
      useGetProjectSubcontractOptionsByProjectId(selectedProject?.id);

    const { data: defaultContractOption, isLoading: isDefaultContractLoading } =
      useGetDefaultProjectSubcontractOptionByProjectId(selectedProject?.id);

    const { data: mainContractOption, isLoading: isMainContractLoading } =
      useGetProjectSubcontractOptionById(selectedProject?.mainContractId);

    const displayContractField = !!selectedProject || false;

    const [pendingContractPreSelection, setPendingContractPreSelection] =
      useState<boolean>(false);

    const updateContractField = useCallback(
      (selectedProject: ProjectSelectOption) => {
        if (selectedProject && contractsOptions.length > 0) {
          if (defaultContractOption) {
            group.setFieldValue('contract', defaultContractOption);
            return;
          }

          if (mainContractOption) {
            group.setFieldValue('contract', mainContractOption);
            return;
          }

          if (contractsOptions.length === 1) {
            group.setFieldValue('contract', contractsOptions[0]);
            return;
          }
        }
      },
      [contractsOptions, defaultContractOption, mainContractOption, group],
    );

    useEffect(() => {
      if (
        pendingContractPreSelection &&
        !!selectedProject &&
        !isContractsLoading &&
        !isDefaultContractLoading &&
        !isMainContractLoading &&
        contractsOptions.length > 0
      ) {
        updateContractField(selectedProject);
        setPendingContractPreSelection(false);
      }
    }, [
      pendingContractPreSelection,
      contractsOptions,
      isContractsLoading,
      isDefaultContractLoading,
      isMainContractLoading,
      selectedProject,
      updateContractField,
    ]);

    // logic to select correct customer on project selection
    const {
      data: customerOptionOfSelectedProject,
      isLoading: isCustomerOptionLoading,
    } = useGetCustomerOptionById(selectedProject?.customerId);

    const expenseFormIsTouched = useStore(
      group.form.store,
      (state) => state.isTouched,
    );

    useEffect(() => {
      /*
       * expenseFormIsTouched is intentionally NOT in the deps array — it's only used as a guard to skip
       * the effect on initial edit form load, not as a trigger. Adding it would cause the effect to fire
       * when the user's first action is clearing the customer field and then adding the customer back.
       */
      if (
        expenseFormIsTouched &&
        !isCustomerOptionLoading &&
        customerOptionOfSelectedProject
      ) {
        group.setFieldValue('customer', customerOptionOfSelectedProject);
      }
    }, [isCustomerOptionLoading, selectedProject]);

    // this is for when we have a pre-selected project on load (e.g., via URL param, navigating from project page)
    useEffect(() => {
      if (selectedProject && !group.state.values.contract) {
        updateContractField(selectedProject);
      }
    }, [selectedProject, contractsOptions, updateContractField]);

    useEffect(() => {
      if (!isUserMeLoading && !hasInitiallyLoaded && !isParentLoading) {
        setHasInitiallyLoaded(true);
      }
    }, [isUserMeLoading, isParentLoading, hasInitiallyLoaded]);
    const showInitialLoadingSkeleton = !hasInitiallyLoaded;

    const fieldsDisabled = formLocked || isParentLoading;

    return (
      <group.FormFieldset legend={title} filled>
        {formLocked && lockedReason && (
          <PageSection.Info>
            {/* This works because FormFieldset wraps PageSection */}
            <LockedExpenseMessage reason={lockedReason} />
          </PageSection.Info>
        )}
        <group.FormGrid>
          {showInitialLoadingSkeleton ? (
            [1, 2, 3].map((index) => <InputFieldSkeletonLoader key={index} />)
          ) : (
            <>
              {expenseType !== 'project' && (
                <group.AppField name="employee">
                  {(Field) => (
                    <Field.SelectInputField
                      required={isRequired(
                        projectInformationSchema.shape.employee,
                      )}
                      label={t('labels.Employee')}
                      disabled={
                        !currentUserCanCreateExpensesForOthers || fieldsDisabled
                      }
                      searchFunction={getSearchEmployeeSelectOptions}
                      typeSenseCollection={TypesenseCollection.EMPLOYEES_VIEW}
                      getOptionByIdFallbackFunction={
                        getEmployeeSelectOptionById
                      }
                    />
                  )}
                </group.AppField>
              )}
              <group.AppField name="customer">
                {(Field) => (
                  <Field.SelectInputField
                    required={isRequired(
                      projectInformationSchema.shape.customer,
                    )}
                    label={t('labels.Customer')}
                    disabled={fieldsDisabled}
                    searchFunction={getSearchCustomerSelectOptions}
                    typeSenseCollection={TypesenseCollection.CUSTOMERS_VIEW}
                    getOptionByIdFallbackFunction={getCustomerSelectOptionById}
                    searchOptions={{
                      filterBy:
                        selectedEmployee &&
                        !allEmployeesCanRegisterOnAllActiveProjects
                          ? `$employeeallocations(UserId:=${selectedEmployee.id})`
                          : undefined,
                    }}
                    dependsOnFields={['employee']}
                  />
                )}
              </group.AppField>
              <group.AppField
                name="project"
                listeners={{
                  onChange: ({ value }) => {
                    // reset contract field always to reset value and meta, to also start fresh for "new" pending pre-selection
                    group.resetField('contract');
                    if (value) {
                      setPendingContractPreSelection(true);
                    }
                  },
                }}
              >
                {(Field) =>
                  expenseType === 'project' ? (
                    <Field.SelectInputField
                      required={isRequired(
                        projectInformationSchema.shape.project,
                      )}
                      label={t('labels.Project')}
                      disabled={fieldsDisabled}
                      searchFunction={getSearchProjectSelectOptionsFromUnion}
                      searchOptions={{
                        filterBy: {
                          [TypesenseCollection.PROJECTS_VIEW.keyName]:
                            getProjectsViewKeyFilter(),
                          [TypesenseCollection.PROJECTS.keyName]:
                            getProjectsKeyFilter(),
                        },
                      }}
                      typeSenseCollection={[
                        TypesenseCollection.PROJECTS_VIEW,
                        TypesenseCollection.PROJECTS,
                      ]}
                      getOptionByIdFallbackFunction={
                        getProjectSelectOptionByIdFromUnion
                      }
                      dependsOnFields={['customer', 'employee']}
                    />
                  ) : (
                    <Field.SelectInputField
                      required={isRequired(
                        projectInformationSchema.shape.project,
                      )}
                      label={t('labels.Project')}
                      disabled={fieldsDisabled}
                      searchFunction={getSearchProjectSelectOptions}
                      searchOptions={{
                        filterBy: getProjectsViewKeyFilter(),
                      }}
                      typeSenseCollection={TypesenseCollection.PROJECTS_VIEW}
                      getOptionByIdFallbackFunction={getProjectSelectOptionById}
                      dependsOnFields={['customer', 'employee']}
                    />
                  )
                }
              </group.AppField>
              {displayContractField && (
                <group.AppField name="contract">
                  {(Field) => (
                    <Field.SelectInputField
                      required={isRequired(
                        projectInformationSchema.shape.contract,
                      )}
                      label={t('labels.Contract')}
                      disabled={fieldsDisabled || isContractsLoading}
                      searchFunction={getSearchProjectSubcontractSelectOptions}
                      searchOptions={{
                        filterBy: selectedProject
                          ? `ProjectId:=${selectedProject.id}`
                          : undefined,
                      }}
                      typeSenseCollection={
                        TypesenseCollection.PROJECT_SUBCONTRACTS
                      }
                      getOptionByIdFallbackFunction={
                        getProjectSubcontractSelectOptionById
                      }
                      dependsOnFields={['project']}
                      endAdornment={
                        isContractsLoading && (
                          <InputAdornment position="end">
                            <CircularProgress
                              size={18}
                              color="inherit"
                              thickness={4}
                            />
                          </InputAdornment>
                        )
                      }
                    />
                  )}
                </group.AppField>
              )}
            </>
          )}
        </group.FormGrid>
      </group.FormFieldset>
    );
  },
});
